import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Card,
  CardContent,
  Chip,
  Drawer,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  FitnessCenter as FitnessCenterIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import RecipeDetails from '../../components/dashboard/RecipeDetails';
import api from '../../services/api';
import { grey, primary, fontSize, fontWeight, radius } from '../../styles/tokens';

// Traduction des jours
const daysShort = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const daysFull = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const mealTranslations = {
  breakfast: 'Petit-dejeuner',
  morningSnack: 'Encas matin',
  lunch: 'Dejeuner',
  snack: 'Gouter',
  dinner: 'Diner',
};

// Config type de jour
const DAY_TYPE_CONFIG = {
  OFF: { label: 'Repos', color: 'default', bgColor: grey[100] },
  rest: { label: 'Repos', color: 'default', bgColor: grey[100] },
  ON: { label: 'Sport', color: 'primary', bgColor: primary[50] },
  sport: { label: 'Sport', color: 'primary', bgColor: primary[50] },
  INTENSIF: { label: 'Intense', color: 'error', bgColor: '#FEE2E2' },
  active_rest: { label: 'Intense', color: 'error', bgColor: '#FEE2E2' },
};

/**
 * Vue Repas Client - Mode Carousel
 * Affiche un jour a la fois avec navigation horizontale
 */
const ClientMeals = () => {
  const { user } = useContext(AuthContext);
  const scrollRef = useRef(null);

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Calcule le lundi de la semaine pour une date donnee
  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    const diff = day === 0 ? -6 : 1 - day; // Si dimanche, reculer de 6 jours
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Determine le jour actuel de la semaine (0 = Lundi, 6 = Dimanche)
  const getCurrentDayIndex = () => {
    const today = new Date();
    const day = today.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    // Convertir: dimanche (0) -> 6, lundi (1) -> 0, etc.
    return day === 0 ? 6 : day - 1;
  };

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const planData = await api.get('/client/weekplan/current');

        if (!planData || !planData.dayPlans) {
          setError('no_plan');
          setLoading(false);
          return;
        }

        // Normaliser client -> user
        if (planData.client && !planData.user) {
          planData.user = planData.client;
        }

        setPlan(planData);
        // Selectionner le jour actuel de la semaine
        setActiveDay(getCurrentDayIndex());
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la recuperation du plan:', err.message);
        if (err.message?.includes('Aucun plan') || err.response?.status === 404) {
          setError('no_plan');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    };

    if (user) {
      fetchCurrentPlan();
    }
  }, [user]);

  // Scroll vers le jour actif dans le carousel
  useEffect(() => {
    if (scrollRef.current && plan) {
      const buttons = scrollRef.current.querySelectorAll('[data-day-button]');
      if (buttons[activeDay]) {
        buttons[activeDay].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeDay, plan]);

  const handleRecipeClick = (meal) => {
    const recipeData = {
      ...meal.recipe,
      name: meal.name || meal.recipeName || meal.recipe?.name,
      macros: meal.macros || meal.recipe?.macros,
      ingredients: meal.ingredients || meal.recipe?.ingredients || [],
      imageUrl: meal.imageUrl || meal.recipe?.imageUrl,
      preparationTime: meal.preparationTime || meal.recipe?.preparationTime,
      cookingTime: meal.cookingTime || meal.recipe?.cookingTime,
      instructions: meal.instructions || meal.recipe?.instructions || '',
      instructionSteps: meal.instructionSteps || meal.recipe?.instructionSteps || [],
    };
    setSelectedRecipe(recipeData);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRecipe(null);
  };

  // Calcule les calories totales par jour
  const calculateTotalCalories = (dayPlan) => {
    let total = 0;
    for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
      const meal = dayPlan.meals?.[mealKey];
      if (meal) {
        total += meal.macros?.calories || meal.recipe?.macros?.calories || 0;
      }
    }
    return total;
  };

  // Date pour chaque jour (basee sur le lundi de la semaine courante)
  const getDayDate = (index) => {
    const monday = getMondayOfWeek(new Date());
    monday.setDate(monday.getDate() + index);
    return monday.getDate();
  };

  // Verifie si c'est aujourd'hui
  const isToday = (index) => {
    return index === getCurrentDayIndex();
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto' }}>
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} variant="rounded" width={56} height={64} />
          ))}
        </Box>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: radius.lg }} />
      </Box>
    );
  }

  // No plan state
  if (error === 'no_plan') {
    return (
      <>
        <Helmet>
          <title>Mes Repas - Yoco</title>
        </Helmet>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            px: 2,
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
          <Typography
            sx={{
              fontSize: fontSize['2xl'],
              fontWeight: fontWeight.semibold,
              color: grey[800],
              mb: 1,
            }}
          >
            Aucun programme actif
          </Typography>
          <Typography
            sx={{
              fontSize: fontSize.base,
              color: grey[500],
              maxWidth: 300,
            }}
          >
            Votre coach n'a pas encore publie de programme pour vous.
          </Typography>
        </Box>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const weekNumber = plan?.weekNumber || '?';
  const currentDayPlan = plan.dayPlans[activeDay];
  const dayTypeConfig = DAY_TYPE_CONFIG[currentDayPlan?.dayType] || DAY_TYPE_CONFIG.OFF;
  const totalCalories = currentDayPlan ? calculateTotalCalories(currentDayPlan) : 0;

  return (
    <>
      <Helmet>
        <title>{`Semaine ${weekNumber} - Mes Repas - Yoco`}</title>
      </Helmet>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Selecteur des jours - En haut */}
        <Box
          ref={scrollRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
          }}
        >
          {plan.dayPlans.map((dayPlan, index) => {
            const isActive = index === activeDay;
            const today = isToday(index);
            const dayDate = getDayDate(index);

            return (
              <Box
                key={index}
                data-day-button
                onClick={() => setActiveDay(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  borderRadius: radius.md,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  bgcolor: isActive ? grey[900] : today ? primary[50] : '#FFFFFF',
                  border: `1px solid ${isActive ? grey[900] : today ? primary[300] : grey[200]}`,
                  '&:hover': {
                    bgcolor: isActive ? grey[900] : grey[100],
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: fontSize.xs,
                    fontWeight: fontWeight.medium,
                    color: isActive ? '#FFFFFF' : today ? primary[600] : grey[500],
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {daysShort[index]}
                </Typography>
                <Typography
                  sx={{
                    fontSize: fontSize.lg,
                    fontWeight: fontWeight.bold,
                    color: isActive ? '#FFFFFF' : today ? primary[600] : grey[900],
                    lineHeight: 1.2,
                  }}
                >
                  {dayDate || index + 1}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Contenu du jour selectionne */}
        {currentDayPlan && (
          <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
            {/* Header du jour */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                bgcolor: dayTypeConfig.bgColor,
                borderBottom: `1px solid ${grey[200]}`,
              }}
            >
              <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: grey[900] }}>
                {daysFull[activeDay]}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip
                  label={dayTypeConfig.label}
                  color={dayTypeConfig.color}
                  size="small"
                  sx={{ fontWeight: 500, height: 24 }}
                />
                <Chip
                  label={`${Math.round(totalCalories)} kcal`}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', height: 24 }}
                />
              </Box>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {/* Seance de sport */}
              {currentDayPlan.workoutSession && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 2,
                    bgcolor: grey[50],
                    borderBottom: `1px solid ${grey[200]}`,
                  }}
                >
                  <FitnessCenterIcon sx={{ color: primary[500], fontSize: 20, mt: 0.5 }} />
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: grey[600], mb: 0.5 }}>
                      Seance du jour
                    </Typography>
                    <Typography sx={{ fontSize: fontSize.sm, color: grey[800], whiteSpace: 'pre-wrap' }}>
                      {currentDayPlan.workoutSession}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Liste des repas */}
              {['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner'].map((mealKey) => {
                const meal = currentDayPlan.meals?.[mealKey];
                if (!meal) return null;

                const mealCalories = meal.macros?.calories || meal.recipe?.macros?.calories || 0;
                const recipeName = meal.name || meal.recipeName || 'Recette';

                return (
                  <Box
                    key={mealKey}
                    onClick={() => handleRecipeClick(meal)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${grey[100]}`,
                      '&:hover': { bgcolor: grey[50] },
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: fontSize.xs, color: grey[500], mb: 0.25 }}>
                        {mealTranslations[mealKey]}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: fontSize.base,
                          fontWeight: fontWeight.medium,
                          color: grey[900],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {recipeName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                      <Typography sx={{ fontSize: fontSize.sm, color: grey[500] }}>
                        {Math.round(mealCalories)} kcal
                      </Typography>
                      <ChevronRightIcon sx={{ fontSize: 20, color: grey[400] }} />
                    </Box>
                  </Box>
                );
              })}

              {/* Message si pas de repas */}
              {!currentDayPlan.meals?.breakfast &&
                !currentDayPlan.meals?.morningSnack &&
                !currentDayPlan.meals?.lunch &&
                !currentDayPlan.meals?.snack &&
                !currentDayPlan.meals?.dinner && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: fontSize.base, color: grey[500] }}>
                      Aucun repas planifie pour ce jour
                    </Typography>
                  </Box>
                )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Drawer pour les details de la recette */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: '100%',
            height: '90vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <RecipeDetails
          recipe={selectedRecipe}
          targetMacros={plan?.user?.targetMacros}
          open={drawerOpen}
          onClose={handleCloseDrawer}
          isMobile={true}
        />
      </Drawer>
    </>
  );
};

export default ClientMeals;
